import {Injectable} from '@angular/core';
import {forkJoin, Observable, of, throwError} from 'rxjs';
import {
    IDevice,
    IDeviceHistoryEventsParams,
    IDeviceHistoryEventsResponse,
    IDeviceInfo,
    IDeviceSystemVolume,
    IDownloadInfoItem,
    IOutdatedDevice,
    IUnapprovedDevice
} from '@interfaces/devices';
import {createHttpParams, removeEmptyPropsFromObj} from '@shared/functions';
import {HttpBackend, HttpClient, HttpParams} from '@angular/common/http';
import {catchError, map, share, switchMap} from 'rxjs/operators';
import {IResponse} from '@interfaces/response';
import {ErrorService} from '@services/error/error.service';
import {DeviceOrientation} from '@shared/enums/device-orientation.enum';
import {IGroup} from '@interfaces/group';
import {Store} from '@ngxs/store';
import {ToggleGlobalLoader} from '@store/loader';
import {IPlaylist} from '@interfaces/playlist';
import {IMediaItem} from '@interfaces/media';
import {environment} from '@env/environment';
import {CookieService} from 'ngx-cookie';
import {ModalService} from '@services/modal/modal.service';
import {osNotSupportedRemoteUpdate} from '@shared/helpers';
import {SizeCountPipe} from '@pipes/size-count/size-count.pipe';
import {getDeviceStorage} from '@shared/device-storage.helpers';
import {TranslateService} from '@ngx-translate/core';
import {DeviceProofMode, DeviceProofResponse} from '@shared/device-proof';

const ONE_YEAR_IN_MS = 365 * 24 * 60 * 60 * 1000;

@Injectable({
    providedIn: 'root',
})

export class DeviceService {
    private readonly sizeCountPipe = new SizeCountPipe();

    constructor(
        private http: HttpClient,
        private errorService: ErrorService,
        private store: Store,
        private httpBackend: HttpBackend,
        private cookie: CookieService,
        private translate: TranslateService,
        private modalService: ModalService,
    ) {
    }

    public setProofMode(guid: string, mode: DeviceProofMode): Observable<DeviceProofResponse> {
        return this.http.post<DeviceProofResponse>(`devices/proof-mode/${guid}`, {mode}).pipe(
            catchError(error => {
                this.errorService.showError(error, false);
                return throwError(() => error);
            })
        );
    }

    private static getDeviceFileDownloadStatus(downloads: IDownloadInfoItem[]): number | null {
        if (!downloads.length) return null;

        let total = 0;
        let downloaded = 0;
        const statusCode = 16;
        for (let i = 0; downloads.length > i; i++) {
            if (downloads[i].status === statusCode) return 0;
            downloaded += downloads[i].downloaded;
            total += downloads[i].total;
        }
        return Math.round(+downloaded / +total * 100);
    }

    private static getDeviceFilePlayableStatus(downloads: IDownloadInfoItem[]): boolean | null {
        if (!downloads.length) return null;

        for (let i = 0; downloads.length > i; i++) {
            if (!downloads[i].displayable) return false;
        }

        return true;
    }

    public getDevices(
        {limit, skip = 0, order = '-id', search = '', noGroup = 0, onSchedule = 0}:
            { limit: number, skip?: number, order?: string, search?: string, noGroup?: 1 | 0, onSchedule?: 1 | 0 }
    ): Observable<IResponse<IDevice>> {
        const params = createHttpParams({
            skip, limit, order, search, noGroup, onSchedule,
        });

        return this.http
            .get<IResponse<IDevice>>('devices', {params})
            .pipe(
                share(),
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public remove(id: number): Observable<any> {
        return this.http.delete(`devices/${id}`)
            .pipe(
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public createDevice(device: IDevice): Observable<any> {
        return this.http.post('devices', device)
            .pipe(
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public removeDeviceFromGroup(groupId: number, ids: number[]): Observable<any> {
        return forkJoin(
            ids.map(id => this.http.delete(`groups/with/${groupId}/device/${id}`))
        ).pipe(
            catchError(err => {
                this.errorService.showError(err);
                return throwError(err);
            })
        );
    }

    public getDevicesInfo(guids: string[]): Observable<{ [key: string]: IDeviceInfo }[]> {
        return this.http
            .post<{ [key: string]: IDeviceInfo }[]>('devices/checkInfoBulk', {guids})
            .pipe(
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public mergeDevicesWithInfo<T extends IDevice | IOutdatedDevice>(devices: T[], info: { [key: string]: IDeviceInfo }[]): T[] {
        if (!info || !devices?.length) return devices;

        return devices.map((device) => {
            const infoItem = info.find(el => el.hasOwnProperty(device.guid));
            if (infoItem?.[device.guid]) return this.addInfoToDevice(device, infoItem[device.guid]);
            return device;
        });
    }

    public addInfoToDevice<T extends IOutdatedDevice | IDevice>(device: T, info: IDeviceInfo): T {
        const newDevice = {...device};
        const newInfo = {...info};
        const downloads = newInfo.downloads;
        newDevice.downloadedStatus = DeviceService.getDeviceFileDownloadStatus(downloads);
        newDevice.playableStatus = DeviceService.getDeviceFilePlayableStatus(downloads);
        newDevice[`info`] = newInfo;
        newDevice[`info`][`avail_mem`] = newInfo.avail_int_mem + newInfo.avail_sd_mem;
        newDevice[`info`][`total_mem`] = newInfo.total_int_mem + newInfo.total_sd_mem;
        return newDevice;
    }

    public isDeviceAppVersionOutdated(device: IDevice | null): boolean {
        if (!device) return false;

        const actualAppVersion = device.info?.actual_app_version_name;
        const currentAppVersion = device.info?.app_version_name;

        return Boolean(actualAppVersion) && currentAppVersion !== actualAppVersion;
    }

    public isDeviceAppSupportRemoteUpdate(device: IDevice | null): boolean {
        if (!device?.info) return false;

        const normalizedOs = device.info.os.replace(' ', '').toLowerCase();
        const isOsSupportRemote = !osNotSupportedRemoteUpdate.some((os) => normalizedOs.includes(os));

        return this.isDeviceAppVersionOutdated(device) && Boolean(device.info.actual_app_version_name) && isOsSupportRemote;
    }

    public calculateCorrectedStartupTime(info: IDeviceInfo): number {
        const savedAtTime = info.savedAt ? new Date(info.savedAt).getTime() : null;
        const deviceStartupTime = new Date(info.system_startup_date).getTime();
        const deviceCurrentTime = new Date(info.system_current_time).getTime();
        const timeOffset = savedAtTime ? savedAtTime - deviceCurrentTime : null;

        return timeOffset !== null ? deviceStartupTime + timeOffset : deviceStartupTime;
    }

    public calculateUptimeSeconds(deviceStartupTime: number, currentTime = Date.now()): number {
        const uptime = currentTime - deviceStartupTime;

        return this.isDurationWithinOneYear(uptime) ? uptime / 1000 : 0;
    }

    public calculateLastUptimeSeconds(info: IDeviceInfo, deviceStartupTime: number): number {
        const lastDisconnectTime = new Date(info.lastDisconnectTime).getTime();
        const lastUptime = lastDisconnectTime - deviceStartupTime;

        return this.isDurationWithinOneYear(lastUptime) ? lastUptime / 1000 : 0;
    }

    private isDurationWithinOneYear(duration: number): boolean {
        return duration <= ONE_YEAR_IN_MS;
    }

    public getUnapprovedDevices(): Observable<IResponse<IUnapprovedDevice>> {
        return this.http.get<IResponse<IUnapprovedDevice>>('devices/unapproved')
            .pipe(
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public editDevice(device: IDevice): Observable<IDevice> {
        return this.http.patch<IDevice>(`devices/${device.id}`, device)
            .pipe(
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                }));
    }

    public getScreenshot(deviceGuid: string): Observable<any> {
        return this.http.post<any>(`devices/preview/${deviceGuid}`, {}, {responseType: 'text' as 'json'})
            .pipe(
                map((src: string) => {
                    return {
                        date: new Date(),
                        scr: src
                    };
                }),
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                }));
    }

    public getScreenshotV2(deviceGuid: string): Observable<any> {
        return this.http.post<any>(`devices/preview/v2/${deviceGuid}`, {}, {responseType: 'json'})
            .pipe(
                map((src: {url: string}) => {
                    return {
                        date: new Date(),
                        scr: src.url
                    };
                }),
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                }));
    }

    public downloadCSV(devices: IDevice[], onlineDeviceGuids?: Set<string>): void {
        if (!devices || devices.length === 0) return;

        const csvHeader = 'Status,Content,ID,Device name,Orientation,In group,Location,Available storage,Total storage,Notes,Serial number,MAC address,Model,Last Connect Time,Last Disconnect Time\n';
        const csvContent = devices
            .map(device => this.setCsvArray([device], onlineDeviceGuids)
                .map(row => row
                    .map(item => item !== null && item !== undefined ? `"${item.toString().replace(/"/g, '""')}"` : '')
                    .join(','))
                .join('\n'))
            .join('\n');

        const csv = csvHeader + csvContent;
        const blob = new Blob(['\uFEFF' + csv], {type: 'text/csv;charset=utf-8;'});
        const url = URL.createObjectURL(blob);

        const hiddenElement = document.createElement('a');
        hiddenElement.href = url;
        hiddenElement.target = '_blank';
        hiddenElement.download = 'devices info.csv';
        document.body.appendChild(hiddenElement);
        hiddenElement.click();
        document.body.removeChild(hiddenElement);
    }

    public addDevicesToGroup(groupId: number, devicesId: number[]): Observable<IGroup[]> {
        const chunks = [];
        const divider = 20;

        while (chunks.length * divider < devicesId.length) {
            const start: number = chunks.length * divider;
            const end = start + divider;

            chunks.push(devicesId.slice(start, end));
        }
        const requests = chunks.map(ids => this.http.post<IGroup>(`groups/with/${groupId}/devices`, {devices: ids}));
        return forkJoin(requests)
            .pipe(
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public deviceInfoController(guid: string, status: boolean): void {
        const infoStatus = status ? 'infoon' : 'infooff';
        const sub = this.http.post(`devices/${infoStatus}/${guid}`, {});
        sub.subscribe({error: (err => this.errorService.showError(err))});
    }

    public restart(guid: string): void {
        const sub = this.http
            .post(`devices/restart/${guid}`, {});
        sub.subscribe({error: (err => this.errorService.showError(err))});
    }

    public approveDevices(guids: string[]): Observable<any> {
        this.store.dispatch(new ToggleGlobalLoader(true));
        const sub = this.http.post('devices/approve', {guids}).pipe(share());
        sub.subscribe({
            next: () => this.store.dispatch(new ToggleGlobalLoader(false)),
            error: (err => {
                this.store.dispatch(new ToggleGlobalLoader(false));
                this.errorService.showError(err);
            })
        });

        return sub;
    }


    public approveDevice(guid: string, device: IDevice): Observable<any> {
        return this.http.post(`devices/approve/${guid}`, {...device})
            .pipe(
                share(),
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public getSingleDeviceInfo(id: number): Observable<IDevice> {
        return this.http.get<IDevice>(`devices/${id}`)
            .pipe(
                share(),
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    getDevicePlaylistById(id: number): Observable<IPlaylist> {
        return this.http.get<IPlaylist>(`devices/playlist/${id}`)
            .pipe(
                share(),
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public getDeviceHistory(input_id: number | string): Observable<IDeviceInfo> {
        return this.http.get<IDeviceInfo>(`devices/history/${input_id}`).pipe(
            catchError(err => {
                this.errorService.showError(err);
                return throwError(err);
            })
        );
    }

    public getDeviceHistoryEvents(
        deviceId: number,
        {types = [], start, end, skip = 0, limit = 20, order = 'DESC'}: IDeviceHistoryEventsParams = {}
    ): Observable<IDeviceHistoryEventsResponse> {
        let params = new HttpParams()
            .set('skip', skip.toString())
            .set('limit', limit.toString())
            .set('order', order);

        types.forEach(type => params = params.append('types', type));
        if (start) params = params.set('start', start);
        if (end) params = params.set('end', end);

        return this.http.get<IDeviceHistoryEventsResponse>(`devices/${deviceId}/history-events`, {params}).pipe(
            catchError(err => {
                this.errorService.showError(err);
                return throwError(err);
            })
        );
    }

    public updateDeviceVolumeSystem(props: Partial<IDeviceSystemVolume>, guid: string): Observable<IDeviceSystemVolume> {
        return this.http.patch<IDeviceSystemVolume>(`devices/system-volume/${guid}`, {...props}).pipe(
            catchError(err => {
                this.errorService.showError(err);
                return throwError(err);
            })
        );
    }


    public getDevicesWithOutdatedApp(limit: number, order: string, skip: number, ): Observable<IResponse<IOutdatedDevice>> {
        const params = createHttpParams({
            skip, limit, order
        });
        return this.http.get<IResponse<IOutdatedDevice>>(`app-updater/devices-with-outdated-app`, {params})
            .pipe(
                share(),
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public updateOutdatedDevices(guids: string[]): Observable<any> {
        return this.http.post('app-updater/devices', {devicesGuids: guids})
            .pipe(
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public updateDevicesOS(guid: string): Observable<any> {
        return this.http.post(`devices/firmware/update/${guid}`, {})
            .pipe(
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    // TODO 04.04.2021 romanzubriichuk: check code below


    public remoteController(guid: string, status: boolean): Observable<any> {
      return this.http.post<Observable<any>>(`devices/${status ? 'irlock' : 'irunlock'}/${guid}`, {})
          .pipe(
              catchError(err => {
                  this.errorService.showError(err);
                  return throwError(err);
              })
          );
    }


    public deviceScreenLockController(guid: string, status: boolean): Observable<any> {
        return this.http.post<Observable<any>>(`devices/${status ? 'screenlock' : 'screenunlock'}/${guid}`, {})
            .pipe(
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    // public getSingleDeviceInfo(id: number): Observable<IDevice> {
    //   return this.http.get<IDevice>(`devices/${id}`)
    // }

    // public checkDeviceForContainingEvents(device: IDevice): boolean {
    //   let isEvent = false;
    //   if (
    //     (device.constant_play) &&
    //     (device.playing_fallback === 0) &&
    //     (
    //       (device.areas &&
    //       (device.areas.length > 0) &&
    //       this.areaService.checkDeviceForMedias(device)) ||
    //       (device.playlist_id && device.playlists_status)
    //     )
    //   ) {
    //     isEvent = true;
    //   }
    //   return isEvent;
    // }

    public resendDeviceSchedule(id: number): Observable<{ message: string }> {
        const stream$ = this.http.post<{ message: string }>(`devices/${id}/resend-schedule`, {}).pipe(
            catchError(err => {
                this.errorService.showError(err, false);
                return throwError(err);
            }),
            share(),
        );

        stream$
            .pipe(
                switchMap(({message}) => message === 'Schedule was resent to the device'
                    ? this.translate.get('app.massage.scheduleResent')
                    : of(message)
                ),
            )
            .subscribe((message: string) => {
                this.modalService.showMsg(message, null, {closePreviousModal: false});
            });

        return stream$;
    }

    public getReport({start, end, media, device, group, playlist}:
                         {
                             start: number, end: number,
                             playlist: IPlaylist | null, media: IMediaItem | null,
                             device: IDevice | null, group: IGroup | null
                         }): Observable<{ key: string }> {
        const params = createHttpParams({
            ...removeEmptyPropsFromObj({
                startPeriod: start,
                endPeriod: end,
                groupId: group?.id,
                deviceId: device?.id,
                playlistId: playlist?.id,
                mediaId: media?.id
            })
        });

        const httpClient = new HttpClient(this.httpBackend);
        const tkn = this.cookie.get('DWallUserToken');

        const req = httpClient.get<{ key: string }>(`${environment.landingUrl}/stats/v1/report`, {
            headers: {
                authorization: `Bearer ${tkn}`,
            }, params
        })
            .pipe(
                share(),
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );

        req.subscribe(({key}: { key: string }) => {
            const hiddenElement = document.createElement('a');
            hiddenElement.href = `${environment.landingUrl}/stats/v1/report/${key}`;
            hiddenElement.target = '_blank';
            hiddenElement.click();
        });

        return req;
    }

    private setCsvArray(devices: IDevice[], onlineDeviceGuids?: Set<string>): (string | null | number | DeviceOrientation)[][] {
        return devices.map((device: IDevice) => {
            const storage = getDeviceStorage(device);
            const isDeviceOnline = onlineDeviceGuids ? onlineDeviceGuids.has(device.guid) : Boolean(device.info);

            return [
                isDeviceOnline ? 'on' : 'off',
                device.downloadedStatus === 100 ? 'downloaded' : 'not downloaded',
                `="${device.input_id}"`,
                device.name ? `="${device.name}"` : '',
                device.orientation,
                device.group_name ? `="${device.group_name}"` : '',
                device.location ? `="${device.location}"` : '',
                storage ? this.sizeCountPipe.transform(storage.available, 0) : '',
                storage ? this.sizeCountPipe.transform(storage.total, 0) : '',
                device.notes,
                device.info?.serial_number || '',
                device.info?.wifi_mac_address || device.wifi?.mac || '',
                device.info?.model || device.wifi?.model || '',
                this.formatCsvDate(device.info?.lastConnectTime),
                this.formatCsvDate(device.info?.lastDisconnectTime),
            ];
        });
    }

    private formatCsvDate(value: number | string | null | undefined): string {
        if (!value) return '';

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';

        const pad = (part: number): string => part.toString().padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    public deviceDownloadManagerController(guid: string, status: boolean): Observable<any> {
        return this.http.post<Observable<any>>(`devices/download-manager/${guid}`, {
            enabled: status,
        }).pipe(
            catchError(err => {
                this.errorService.showError(err);
                return throwError(err);
            })
        );
    }

    public deviceDebugModeController(guid: string, status: boolean): Observable<any> {
        return this.http.post<Observable<any>>(`devices/debug-mode/${guid}`, {
            enabled: status,
        }).pipe(
            catchError(err => {
                this.errorService.showError(err);
                return throwError(err);
            })
        );
    }

    public deviceCollectLogsController(guid: string, status: boolean): Observable<any> {
        return this.http.post<Observable<any>>(`devices/logs-collection/${guid}`, {
            enabled: status,
        }).pipe(
            catchError(err => {
                this.errorService.showError(err);
                return throwError(err);
            })
        );
    }
}
